import React, { useEffect, useRef, useState } from "react";
import "./summary.scss";
import { Row, Col, Typography, Tooltip, Spin, Table, Space, Button, Input, TableProps, List, Modal } from "antd";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import Icon from "@assets/icon_config";
import Highlighter from "react-highlight-words";
import { get, isEmpty } from "lodash";
import { TableLocale } from "@components/noDataFound";
import { SearchOutlined } from "@ant-design/icons";
import { ColumnType } from "antd/es/table";
import { CSVLink } from "react-csv";
import { formatDownloadFileNameDate } from "@utils/helpers";
import { usePatientSummaryStore } from "@stores/patientSummaryStore";
import { useDropdowns } from "@utils/customHooks";
import MultiSelectColFilters from "@components/filters/multiSelectColFilters";
import memberDetailsService from "@services/memberDetails.service";
import { FilterValue } from "antd/lib/table/interface";
const { Title, Text } = Typography;

const SummaryProgressBar = ({ pcpVisits, specialistVisits }: any) => {
  const totalOfficeVisits = pcpVisits + specialistVisits;
  const minWidthPercentage = 5;
  let accumulatedPercentage = 0;
  const data = [
    { label: "PCP Visits", value: pcpVisits, color: "#7DC7F8" },
    { label: "Specialist Visits", value: specialistVisits, color: "#6184D9" },
  ];

  return (
    <div className="summary-progress-bar">
      {totalOfficeVisits === 0 ? (
        <div className="summary-progress-bar__no-visits f14 w400">No Visits</div>
      ) : (
        data.map((segment, index) => {
          const width = (segment.value / totalOfficeVisits) * 100;
          const displayWidth = width < minWidthPercentage ? minWidthPercentage : width;
          const style = {
            backgroundColor: segment.color,
            width: `${displayWidth}%`,
            left: `${accumulatedPercentage}%`,
          };
          accumulatedPercentage += displayWidth;

          return (
            <Tooltip title={`${segment.label}: ${segment.value}`} key={index}>
              <div className="summary-progress-bar__visits f14 w400" style={style}>
                {segment.value}
              </div>
            </Tooltip>
          );
        })
      )}
    </div>
  );
};

interface PatientSummaryProps {
  summaryData: { [key: string]: any };
  diagnosisCodeData: any[];
  setDiagnosisCodeData: (x: any) => void;
  summaryLoader: boolean;
  sortStrings: (a: any, b: any, dataIndex: string) => number;
  columnPlaceholder: { [key: string]: string };
  patientID: string;
  prevPatientID: any;
  className: string;
}

const PatientSummary: React.FC<PatientSummaryProps> = ({ summaryData, diagnosisCodeData, setDiagnosisCodeData, summaryLoader, sortStrings, columnPlaceholder, patientID, className }) => {
  const searchInput = useRef<any>(null);
  const columnSearchInfo = usePatientSummaryStore((state) => state.patientSearchText);
  const setColumnSearchInfo = usePatientSummaryStore((state) => state.setPatientSearchText);
  const [hccCategoryFilterList, setHCCCategoryFilterList] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const { getOptions, filterDropdowns } = useDropdowns();
  const legendTitles = ["PCP Visits", "Specialist Visits"];
  const legendColors = ["pcpVisit", "specialistVisit"];
  const totalCostsFormatted = isEmpty(summaryData) ? "--" : `$ ${summaryData?.totalCosts.toLocaleString()}`;

  const displayValue = (value: any) => {
    return isEmpty(summaryData) ? "--" : value;
  };

  const fetchColumnFilterList = (attributeName: string, payload: any) => {
    memberDetailsService.fetchAttributeDropdownWithSqParams({ attributeName, sqlParamsFromPayload: [payload] }).subscribe({
      next: (response: any) => {
        setHCCCategoryFilterList(response?.data?.data?.data);  
      },
      error: (error) => console.error(error),
    });
  };

  useEffect(() => {
    getOptions('EOLFlagFilterLst');
      fetchColumnFilterList('HCCCategoryLst', patientID)
      setColumnSearchInfo({});
  }, [patientID, setColumnSearchInfo]);
  
  const showModal = (providers: any) => {
    setSelectedProviders(providers);
    setModalVisible(true);
  };

  const applyColumnSearch = (dataIndex: any, text: any) => {
    if (text === "") {
      setDiagnosisCodeData(summaryData?.uniqueDiagnosisCodeDetails);
    } else {
      const filteredTableData = summaryData?.uniqueDiagnosisCodeDetails?.filter((item: any) => {
        const value = item[dataIndex];
        return value ? value.toString().toLowerCase().includes(text.toLowerCase()) : false;
      });
      setDiagnosisCodeData(filteredTableData);
    }
  };

  const columnSearch = (dataIndex: any): ColumnType<any> => ({
    filterDropdown: ({ confirm, clearFilters }: any) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${columnPlaceholder[dataIndex] || dataIndex}`}
          value={get(columnSearchInfo, dataIndex, "")}
          onChange={(e) => {
            setColumnSearchInfo({
              ...columnSearchInfo,
              [dataIndex]: e.target.value,
            });
          }}
          onPressEnter={() => {
            applyColumnSearch(dataIndex, columnSearchInfo[dataIndex] || "");
            confirm({ closeDropdown: true });
          }}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
            onClick={() => {
              applyColumnSearch(dataIndex, columnSearchInfo[dataIndex] || "");
              confirm({ closeDropdown: true });
            }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              setColumnSearchInfo((prev: any) => ({ ...prev, [dataIndex]: "" }));
              applyColumnSearch(dataIndex, "");
              clearFilters();
              confirm({ closeDropdown: true });
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: () => (
      <SearchOutlined
        style={{
          color: get(columnSearchInfo, dataIndex, "") !== "" ? "#EF6D2A" : undefined,
        }}
      />
    ),
    onFilter: (value, record) => {
      const cellValue = record[dataIndex];
      if (cellValue != null) {
        return cellValue
          .toString()
          .toLowerCase()
          .includes((value as string).toLowerCase());
      }
      return false;
    },
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text: any, record: any) => {
      if (dataIndex === "diagnosisCode") {
        return (
          <Button type="link" onClick={() => showModal(record?.providerDetails)}>
            {text}
          </Button>
        );
      } else {
        return <Highlighter highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }} searchWords={[columnSearchInfo[dataIndex]]} autoEscape textToHighlight={text ? text.toString() : "-"} />;
      }
    },
  });

  const handleChange: TableProps<any>['onChange'] = (_, filters, __) => {
    setFilteredInfo(filters);
  };

  const tableColumns = [
    {
      title: "HCC CATEGORY",
      dataIndex: "hccCategory",
      width: "8%",
      sorter: (a: any, b: any) => sortStrings(a, b, "hccCategory"),
      filteredValue: filteredInfo.hccCategory || null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, visible }: any) => (
        <MultiSelectColFilters
          filterDropdowns={hccCategoryFilterList}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          placeholder={columnPlaceholder?.hccCategory}
          visible={visible}
          setFilteredInfo={setFilteredInfo}
          includeSelectAllAndBlanks={true}
        />
      ),
      onFilter: (value: any, record: any) => {
        if (value === "blanks") return !record.hccCategory || record.hccCategory.trim() === "";
        if (record.hccCategory) {
          const hccCategories = record?.hccCategory.split(",").map((item: any) => item.trim());
          return hccCategories?.includes(value.toString());
        }
        return false;
      },
      filterIcon: (value: any) => <Icon name="funnel" colorOfBrand={value ? "#ef6d2a" : ""} />,
      render: (text: any) => {
        return text ? text.toString().replace(/,/g, ", ") : text || "-";
      },
    },
    {
      title: "HCC CAPTURED IN CURRENT YEAR",
      dataIndex: "hccCapturedInCurrentYear",
      width: "12%",
      ellipsis: {
        showTitle: false,
      },
      sorter: (a: any, b: any) => sortStrings(a, b, "hccCapturedInCurrentYear"),
      onFilter: (value: any, record: any) => record.hccCapturedInCurrentYear === value,
      filterIcon: (value: any) => <Icon name="funnel" colorOfBrand={value ? "#ef6d2a" : ""} />,
      filteredValue: filteredInfo.hccCapturedInCurrentYear || null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, visible, clearFilters }: any) => (
        <MultiSelectColFilters
          filterDropdowns={filterDropdowns?.EOLFlagFilterLst ? [...filterDropdowns?.EOLFlagFilterLst, { value: "New", label: "New" }] : []}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          placeholder={columnPlaceholder?.hccCapturedInCurrentYear}
          visible={visible}
          setFilteredInfo={setFilteredInfo}
        />
      ),
      render: (text: string) => {
        return text ? text.toString() : "-";
      },
    },
    {
      title: "DIAGNOSIS CODES",
      dataIndex: "diagnosisCode",
      width: "10%",
      ...columnSearch("diagnosisCode"),
      sorter: (a: any, b: any) => sortStrings(a, b, "diagnosisCode"),
    },
    {
      title: "DESCRIPTION",
      dataIndex: "description",
      sorter: (a: any, b: any) => sortStrings(a, b, "description"),
      ...columnSearch("description"),
      width: "20%",
    },
    {
      title: "IS V28?",
      dataIndex: "isV28",
      width: "8%",
      ellipsis: {
        showTitle: false,
      },
      sorter: (a: any, b: any) => sortStrings(a, b, "isV28"),
      onFilter: (value: any, record: any) => record.isV28 === value,
      filterIcon: (value: any) => <Icon name="funnel" colorOfBrand={value ? "#ef6d2a" : ""} />,
      filteredValue: filteredInfo.isV28 || null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, visible, clearFilters }: any) => (
        <MultiSelectColFilters
          filterDropdowns={filterDropdowns?.EOLFlagFilterLst}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          placeholder={columnPlaceholder?.isV28}
          visible={visible}
          setFilteredInfo={setFilteredInfo}
        />
      ),
      render: (text: string) => {
        return text ? text.toString() : "-";
      },
    },
    {
      title: "ICD CAPTURED IN CURRENT YEAR",
      dataIndex: "icdCapturedInCurrentYear",
      width: "12%",
      ellipsis: {
        showTitle: false,
      },
      sorter: (a: any, b: any) => sortStrings(a, b, "icdCapturedInCurrentYear"),
      onFilter: (value: any, record: any) => record.icdCapturedInCurrentYear === value,
      filterIcon: (value: any) => <Icon name="funnel" colorOfBrand={value ? "#ef6d2a" : ""} />,
      filteredValue: filteredInfo.icdCapturedInCurrentYear || null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, visible, clearFilters }: any) => (
        <MultiSelectColFilters
          filterDropdowns={filterDropdowns?.EOLFlagFilterLst ? [...filterDropdowns?.EOLFlagFilterLst, { value: "New", label: "New" }] : []}
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          placeholder={columnPlaceholder?.icdCapturedInCurrentYear}
          visible={visible}
          setFilteredInfo={setFilteredInfo}
        />
      ),
      render: (text: string) => {
        return text ? text.toString() : "-";
      },
    },
  ];

  const isDataAvailable = diagnosisCodeData?.length > 0;

  return (
    <Spin spinning={summaryLoader} indicator={<HealthemSpinner size={70} />}>
      <Row>
        <Col span={8}>
          <Title level={5}>Total Costs</Title>
          <Title level={3} style={{ marginTop: 0, color: "#091E42" }}>
            {totalCostsFormatted}
          </Title>
          <Title level={5} className={`${className}__title`}>
            ER Summary
          </Title>

          <Row style={{ marginTop: 16 }}>
            <Col span={12}>
              <Text className=" f16 w400">ER Visits</Text>
            </Col>
            <Col span={12}>
              <Title level={5} className="">
                {displayValue(summaryData?.erVisits)}
              </Title>
            </Col>
          </Row>

          <Row style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text className=" f16 w400">ER Admissions</Text>
            </Col>
            <Col span={12}>
              <Title level={5} className="">
                {displayValue(summaryData?.erAdmissions)}
              </Title>
            </Col>
          </Row>
        </Col>
        <Col span={14}>
          <Row>
            <Col span={6} style={{ display: "flex", flexDirection: "column" }}>
              <div>
                <Title level={5}>Office Visits</Title>
              </div>
              <Row>
                <Col span={12} style={{ textAlign: "center" }}>
                  <Title level={4} style={{ margin: 0, color: "#091E42" }}>
                    {displayValue(summaryData?.pcpVisits + summaryData?.specialistVisits)}
                  </Title>
                </Col>
              </Row>
            </Col>
            <Col span={18}>
              <div className="summary-wrapper__ui-csd-legend">
                {legendTitles.map((title, index) => (
                  <React.Fragment key={index}>
                    <div className={`summary-wrapper__ui-csd-legend__indicator ${legendColors[index]}`}></div>
                    <span style={{ marginRight: 25 }}>{title}</span>
                  </React.Fragment>
                ))}
              </div>
              <SummaryProgressBar pcpVisits={summaryData?.pcpVisits} specialistVisits={summaryData?.specialistVisits} />
            </Col>
          </Row>
          <Row className={`${className}__title`}>
            <Col span={9}>
              <Title level={5}>Hospital Admissions</Title>
            </Col>
            <Col span={15}>
              <Title level={5}>{displayValue(summaryData?.hospitalAdmissions)}</Title>
            </Col>
          </Row>
          <Row style={{ marginTop: 8 }}>
            <Col span={9}>
              <Title level={5}>Outpatient Visits</Title>
            </Col>
            <Col span={15}>
              <Title level={5} className="">
                {displayValue(summaryData?.outpatientVisits)}
              </Title>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row justify="space-between" align="middle" style={{ marginTop: 15 }}>
        <Title level={5}>Unique Diagnosis Codes</Title>
        <div className={`${className}__table__download-icon`}>
          <Tooltip title={isDataAvailable ? "Download XLS" : "No Data"} overlayClassName="download-tooltip">
            <div style={{ cursor: isDataAvailable ? "pointer" : "not-allowed" }}>
              {isDataAvailable ? (
                <CSVLink
                  data={diagnosisCodeData?.map((row: any) => ({
                    ...row,
                    hccCategory: row.hccCategory || "-",
                    hccCapturedInCurrentYear: row.hccCapturedInCurrentYear || "-",
                    diagnosisCode: row.diagnosisCode || "-",
                    description: row.description || "-",
                    isV28: row.isV28 || "-",
                    icdCapturedInCurrentYear: row.icdCapturedInCurrentYear || "-",
                  }))}
                  headers={[
                    { label: "HCC CATEGORY", key: "hccCategory" },
                    { label: "HCC CAPTURED IN CURRENT YEAR", key: "hccCapturedInCurrentYear" },
                    { label: "DIAGNOSIS CODES", key: "diagnosisCode" },
                    { label: "DESCRIPTION", key: "description" },
                    { label: "IS V28?", key: "isV28" },
                    { label: "ICD CAPTURED IN CURRENT YEAR", key: "icdCapturedInCurrentYear" },
                  ]}
                  filename={`Unique Diagnosis Codes_${formatDownloadFileNameDate(new Date())}`}
                >
                  <i
                    className={`btn-icon icon-download clr-n125 ${!isDataAvailable ? "disabled" : ""}`}
                    style={{
                      width: "20px",
                      height: "42px",
                      opacity: 1,
                    }}
                  />
                </CSVLink>
              ) : (
                <i
                  className={`btn-icon icon-download clr-n125 disabled`}
                  style={{
                    width: "20px",
                    height: "42px",
                    opacity: 0.5,
                  }}
                />
              )}
            </div>
          </Tooltip>
        </div>
      </Row>
      <Row style={{ marginTop: 10 }}>
        <Table
          bordered={true}
          pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ["5", "10", "15", "20", "25", "30"] }}
          columns={tableColumns}
          dataSource={diagnosisCodeData}
          locale={TableLocale}
          onChange={handleChange}
          scroll={{ x: "max-content" }}
          style={{ width: "100%" }}
        />
        <Modal title="Details" centered visible={modalVisible} onCancel={() => setModalVisible(false)} footer={null} bodyStyle={{ maxHeight: "210px", overflowY: "auto", padding: "0 25px" }}>
          <List
            dataSource={selectedProviders}
            renderItem={(item: any) => (
              <List.Item className={`${className}__popup-list`}>
                <List.Item.Meta
                  description={
                    <>
                      <span className={`${className}__popup-list__label`}>Provider Name: </span>
                      {item.provider}
                      <br />
                      <span className={`${className}__popup-list__label`}>DOS: </span>
                      {item.dos}
                      <br />
                      <span className={`${className}__popup-list__label`}>PCP Vendor Name: </span>
                      {item.vendor}
                    </>
                  }
                />
              </List.Item>
            )}
          />
        </Modal>
      </Row>
    </Spin>
  );
};

export default PatientSummary;
